// Copied from DealerFlow 360 (dealerflow360/web/src/shared/payments/acceptJs.ts); spec references are DF360's.
// In the LOS the same holds: card digits never reach los-api (LOS card down payments A5).
/**
 * Accept.js: the browser sends the card straight to Authorize.Net and gets back a single-use
 * token (spec R20-R24a). Card digits never reach DealerFlow 360's API.
 *
 * Ported from DriveAway's loader (customer-loan-management-portal/src/services/AcceptJs.ts):
 * Accept.js first installs a stub `dispatchData` that answers E_WC_03 "Accept.js is not loaded
 * correctly"; the real one exists only once AcceptCore.js has loaded and set `window.isReady`.
 * So readiness is `window.isReady`, not the script's load event or `window.Accept`.
 *
 * Added here:
 * - Single flight: one load at a time, shared by every caller. A tag that fails (error or
 *   timeout) is removed and forgotten, so the next call injects a fresh one.
 * - One environment per page session (R22): Accept.js lives on `window`, so once a script has
 *   run, a different script URL (Sandbox vs Production) is refused until the page reloads.
 * - One 15 s budget for download and readiness together (R24a).
 */

export interface AcceptAuthData {
  apiLoginId: string;
  clientKey: string;
  scriptUrl: string;
}

export interface AcceptCardData {
  /** Digits only. */
  cardNumber: string;
  /** Two digits. */
  month: string;
  /** Four digits. */
  year: string;
  cardCode: string;
  zip?: string;
  fullName?: string;
}

export interface AcceptOpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

interface AcceptMessage {
  code?: string;
  text?: string;
}

interface AcceptResponse {
  opaqueData?: Partial<AcceptOpaqueData> | null;
  messages?: { resultCode?: string; message?: AcceptMessage[] | null } | null;
}

declare global {
  interface Window {
    Accept?: {
      dispatchData: (
        data: {
          authData: { apiLoginID: string; clientKey: string };
          cardData: AcceptCardData;
        },
        callback: (response: AcceptResponse) => void,
      ) => void;
    };
    /** Set by Accept.js once AcceptCore.js has loaded and finished its handshake. */
    isReady?: boolean;
  }
}

/** R24a. */
export const ACCEPT_LOAD_FAILED =
  "Authorize.Net's card form didn't load. Check your connection and try again.";
/** R22. */
export const ACCEPT_RELOAD_REQUIRED = 'Reload the page to use this processor account.';
export const ACCEPT_NO_TOKEN = "Authorize.Net didn't return a card token. Nothing was charged.";
export const ACCEPT_NO_ANSWER = "Authorize.Net's card form didn't answer. Nothing was charged. Try again.";

export const ACCEPT_READY_TIMEOUT_MS = 15_000;
const READY_POLL_MS = 100;
/** dispatchData answers in a second or two; past this the token is abandoned. */
const DISPATCH_TIMEOUT_MS = 30_000;

/** An Accept.js failure. `messages` holds Authorize.Net's own codes and texts, when it sent any. */
export class AcceptJsError extends Error {
  readonly messages: { code: string; text: string }[];

  constructor(message: string, messages: { code: string; text: string }[] = []) {
    super(message);
    this.name = 'AcceptJsError';
    this.messages = messages;
  }
}

/** Authorize.Net's messages as staff see them: "Card number is invalid. (E_WC_05)" (R24). */
export function acceptMessageText(messages: readonly AcceptMessage[]): string[] {
  return messages
    .map(({ code, text }) => {
      const body = text?.trim() ?? '';
      const tag = code?.trim() ? `(${code.trim()})` : '';
      return [body, tag].filter(Boolean).join(' ');
    })
    .filter(Boolean);
}

// The page session's one script. `sessionScriptUrl` is the environment this page is bound to.
let sessionScriptUrl: string | null = null;
let inFlight: Promise<void> | null = null;
let activeTag: HTMLScriptElement | null = null;

const ready = () => window.isReady === true && !!window.Accept;

/** True when this page already ran another environment's script, so this one can't load (R22). */
export function acceptEnvironmentBlocked(scriptUrl: string | null | undefined): boolean {
  return !!scriptUrl && !!sessionScriptUrl && sessionScriptUrl !== scriptUrl;
}

/**
 * Loads Accept.js from `scriptUrl` and resolves once `window.isReady` is set. Shared by every
 * caller while it runs; resolves at once when this page has it ready.
 */
export function loadAcceptJs(scriptUrl: string, timeoutMs = ACCEPT_READY_TIMEOUT_MS): Promise<void> {
  if (acceptEnvironmentBlocked(scriptUrl)) {
    return Promise.reject(new AcceptJsError(ACCEPT_RELOAD_REQUIRED));
  }
  if (sessionScriptUrl === scriptUrl && ready()) return Promise.resolve();
  if (inFlight) return inFlight;

  sessionScriptUrl = scriptUrl;
  const tag = document.createElement('script');
  tag.src = scriptUrl;
  tag.async = true;
  tag.charset = 'utf-8';
  activeTag = tag;

  const attempt = new Promise<void>((resolve, reject) => {
    let poll: number | undefined;
    const finish = (error?: AcceptJsError) => {
      window.clearTimeout(deadline);
      window.clearInterval(poll);
      tag.onload = null;
      tag.onerror = null;
      if (inFlight === attempt) inFlight = null;
      if (!error) {
        resolve();
        return;
      }
      // Forget the tag so the next try injects a fresh one. Until a script has run, the page is
      // not bound to its environment.
      tag.remove();
      if (activeTag === tag) activeTag = null;
      if (!window.Accept) sessionScriptUrl = null;
      reject(error);
    };
    const deadline = window.setTimeout(() => finish(new AcceptJsError(ACCEPT_LOAD_FAILED)), timeoutMs);
    tag.onerror = () => finish(new AcceptJsError(ACCEPT_LOAD_FAILED));
    tag.onload = () => {
      if (ready()) {
        finish();
        return;
      }
      poll = window.setInterval(() => {
        if (ready()) finish();
      }, READY_POLL_MS);
    };
  });
  inFlight = attempt;
  document.head.appendChild(tag);
  return attempt;
}

/**
 * Sends the card to Authorize.Net and returns the opaque token. Accept.js must be ready
 * (`loadAcceptJs`). Rejects with Authorize.Net's messages on `resultCode` Error, and when a
 * success carries no token.
 */
export function dispatchCard(auth: AcceptAuthData, cardData: AcceptCardData): Promise<AcceptOpaqueData> {
  const accept = window.Accept;
  if (!accept || window.isReady !== true) return Promise.reject(new AcceptJsError(ACCEPT_LOAD_FAILED));
  return new Promise((resolve, reject) => {
    let answered = false;
    const timer = window.setTimeout(() => {
      answered = true;
      reject(new AcceptJsError(ACCEPT_NO_ANSWER));
    }, DISPATCH_TIMEOUT_MS);
    accept.dispatchData(
      { authData: { apiLoginID: auth.apiLoginId, clientKey: auth.clientKey }, cardData },
      (response) => {
        if (answered) return;
        answered = true;
        window.clearTimeout(timer);
        const messages = (response?.messages?.message ?? []).map((m) => ({
          code: m?.code?.trim() ?? '',
          text: m?.text?.trim() ?? '',
        }));
        if (response?.messages?.resultCode === 'Error') {
          const lines = acceptMessageText(messages);
          reject(new AcceptJsError(lines.join(' ') || 'Authorize.Net could not read the card.', messages));
          return;
        }
        const descriptor = response?.opaqueData?.dataDescriptor;
        const value = response?.opaqueData?.dataValue;
        if (!descriptor || !value) {
          reject(new AcceptJsError(ACCEPT_NO_TOKEN));
          return;
        }
        resolve({ dataDescriptor: descriptor, dataValue: value });
      },
    );
  });
}

/** Load (once) and dispatch. */
export async function tokenizeCard(auth: AcceptAuthData, cardData: AcceptCardData): Promise<AcceptOpaqueData> {
  await loadAcceptJs(auth.scriptUrl);
  return dispatchCard(auth, cardData);
}

/** Test seam: forget the page session's script and environment. */
export function resetAcceptJsForTests(): void {
  activeTag?.remove();
  activeTag = null;
  inFlight = null;
  sessionScriptUrl = null;
}
